import { useEffect, useState } from "react";
import { FiPlus, FiSave, FiTrash2, FiX } from "react-icons/fi";
import { apiRequest } from "../../services/api";
import PurchaseOrderProductFilterModal from "./components/PurchaseOrderProductFilterModal";

const numberFields = [
  "quantityReceived",
  "quantityAccepted",
  "quantityRejected",
  "unitCost",
  "mrp",
  "posPrice",
  "onlinePrice",
  "cashDiscountPercent",
  "tradeDiscountPercent",
  "specialDiscountPercent",
  "supplierTaxRate1",
  "supplierTaxRate3",
];
const numericColumns = [
  ["quantityReceived", "Received"],
  ["quantityAccepted", "Accepted"],
  ["quantityRejected", "Rejected"],
  ["unitCost", "Basic rate"],
  ["mrp", "MRP"],
  ["posPrice", "POS price"],
  ["onlinePrice", "Online price"],
  ["cashDiscountPercent", "C.D. %"],
  ["tradeDiscountPercent", "T.D. %"],
  ["specialDiscountPercent", "SPCD %"],
];
const rows = (value) =>
  Array.isArray(value) ? value : value?.data || value?.items || [];
const inputClass =
  "h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100";
const lineFromReceipt = (line) => ({
  ...line,
  ...Object.fromEntries(
    numberFields.map((key) => [
      key,
      line[key] == null ? "" : String(line[key]),
    ]),
  ),
  expiryDate: line.expiryDate?.slice(0, 10) || "",
  batchNumber: line.batchNumber || "",
  notes: line.notes || "",
});

export default function GrnEditModal({ grn, accessToken, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    supplierId: grn.supplierId,
    storeId: grn.storeId,
    seasonId: grn.seasonId,
    purchaseOrderId: grn.purchaseOrderId || undefined,
    supplierInvoiceNumber: grn.supplierInvoiceNumber || "",
    supplierInvoiceDate: grn.supplierInvoiceDate?.slice(0, 10) || "",
    receivedAt: grn.receivedAt,
    notes: grn.notes || "",
    freightAmount: String(grn.freightAmount || 0),
    freightTaxRate: String(grn.freightTaxRate || 0),
    editReason: "",
  }));
  const [items, setItems] = useState(() => grn.items.map(lineFromReceipt));
  const [options, setOptions] = useState({
    suppliers: [],
    stores: [],
    seasons: [],
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [productPicker, setProductPicker] = useState(false);
  useEffect(() => {
    let active = true;
    Promise.all(
      ["/suppliers", "/inventory/stores", "/seasons"].map((url) =>
        apiRequest(url, { accessToken }),
      ),
    )
      .then(([suppliers, stores, seasons]) => {
        if (active)
          setOptions({
            suppliers: rows(suppliers),
            stores: rows(stores),
            seasons: rows(seasons),
          });
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      active = false;
      document.body.style.overflow = previous;
    };
  }, [accessToken]);
  const field = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const changeLine = (index, key, value) =>
    setItems((current) =>
      current.map((line, i) =>
        i === index ? { ...line, [key]: value } : line,
      ),
    );
  const addProducts = (variants) => {
    setItems((current) => [
      ...current,
      ...variants
        .filter(
          (variant) => !current.some((line) => line.variantId === variant.id),
        )
        .map((variant) =>
          lineFromReceipt({
            variantId: variant.id,
            variant,
            quantityReceived: 1,
            quantityAccepted: 1,
            quantityRejected: 0,
            unitCost: variant.purchasePrice ?? variant.lastPurchasePrice ?? 0,
            mrp: variant.mrp ?? "",
            posPrice: variant.posPrice ?? variant.mrp ?? "",
            onlinePrice: variant.onlinePrice ?? "",
            cashDiscountPercent: 0,
            tradeDiscountPercent: 0,
            specialDiscountPercent: 0,
            isDefaultOnlineLot: false,
          }),
        ),
    ]);
    setProductPicker(false);
  };
  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!items.length) {
      setError("Add at least one product.");
      return;
    }
    const payload = {
      ...form,
      freightAmount: Number(form.freightAmount),
      freightTaxRate: Number(form.freightTaxRate),
      supplierInvoiceDate: form.supplierInvoiceDate || undefined,
      expectedUpdatedAt: grn.updatedAt,
      items: items.map((line) => ({
        variantId: line.variantId,
        purchaseOrderItemId: line.purchaseOrderItemId || undefined,
        ...Object.fromEntries(
          numberFields.map((key) => [
            key,
            line[key] === "" ? undefined : Number(line[key]),
          ]),
        ),
        batchNumber: line.batchNumber || undefined,
        expiryDate: line.expiryDate || undefined,
        notes: line.notes || undefined,
        isDefaultOnlineLot: !!line.isDefaultOnlineLot,
      })),
    };
    if (
      payload.items.some((line) =>
        numberFields.some(
          (key) =>
            line[key] !== undefined &&
            (!Number.isFinite(line[key]) || line[key] < 0),
        ),
      )
    ) {
      setError("Enter valid non-negative numbers.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const result = await apiRequest(`/purchases/grns/${grn.id}`, {
        accessToken,
        method: "PATCH",
        body: payload,
      });
      onSaved(result);
    } catch (e) {
      setError(e.message || "Unable to update GRN. No changes were saved.");
    } finally {
      setSaving(false);
    }
  };
  const select = (key, label, values, current) => (
    <label className="space-y-2 text-sm font-medium">
      {label}
      <select
        required
        disabled={
          saving ||
          (Boolean(grn.agreementType) && key === 'supplierId') ||
          (!!grn.purchaseOrderId && ["supplierId", "storeId"].includes(key))
        }
        className={inputClass}
        value={form[key]}
        onChange={(e) => field(key, e.target.value)}
      >
        {!values.some((value) => value.id === form[key]) && current && (
          <option value={current.id}>
            {current.displayName || current.name}
          </option>
        )}
        {values.map((value) => (
          <option key={value.id} value={value.id}>
            {value.displayName || value.name}
            {value.code ? ` (${value.code})` : ""}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <>
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="grn-edit-title"
      >
        <form
          onSubmit={save}
          className="flex max-h-[94vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        >
          <header className="flex items-center justify-between border-b px-6 py-4">
            <div>
              <h2 id="grn-edit-title" className="text-xl font-bold">
                Edit {grn.grnNumber}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {grn.status === "POSTED"
                  ? "Posted receipt — stock, prices, GST and totals will be revised together."
                  : "Update draft receipt details."}
              </p>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              aria-label="Close GRN editor"
              className="p-3"
            >
              <FiX size={22} />
            </button>
          </header>
          <div className="overflow-y-auto p-6 space-y-6">
            {error && (
              <p
                role="alert"
                className="rounded-xl bg-red-50 p-4 text-sm text-red-700"
              >
                {error}
              </p>
            )}
            {grn.status === "POSTED" && (
              <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
                Invoice details and notes can be corrected after stock
                movements. Product, quantity, cost or lot changes are blocked if
                affected stock has been sold, returned, reserved or transferred.
                Every save is audited.
              </p>
            )}
            <div className="grid gap-4 md:grid-cols-3">
              {select(
                "supplierId",
                "Supplier",
                options.suppliers,
                grn.supplier,
              )}
              {select(
                "storeId",
                "Receiving store / branch",
                options.stores,
                grn.store,
              )}
              {select("seasonId", "Season", options.seasons, grn.season)}
              <label className="space-y-2 text-sm font-medium">
                Supplier invoice no.
                <input
                  className={inputClass}
                  maxLength={100}
                  value={form.supplierInvoiceNumber}
                  onChange={(e) =>
                    field("supplierInvoiceNumber", e.target.value)
                  }
                />
              </label>
              <label className="space-y-2 text-sm font-medium">
                Supplier invoice date
                <input
                  type="date"
                  className={inputClass}
                  value={form.supplierInvoiceDate}
                  onChange={(e) => field("supplierInvoiceDate", e.target.value)}
                />
              </label>
              <label className="space-y-2 text-sm font-medium">
                Received date
                <input
                  required
                  type="date"
                  className={inputClass}
                  value={form.receivedAt?.slice(0, 10)}
                  onChange={(e) =>
                    field(
                      "receivedAt",
                      e.target.value ? `${e.target.value}T00:00:00.000Z` : "",
                    )
                  }
                />
              </label>
              <label className="space-y-2 text-sm font-medium">
                Freight excl. GST
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputClass}
                  value={form.freightAmount}
                  onChange={(e) => field("freightAmount", e.target.value)}
                />
              </label>
              <label className="space-y-2 text-sm font-medium">
                Freight GST %
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className={inputClass}
                  value={form.freightTaxRate}
                  onChange={(e) => field("freightTaxRate", e.target.value)}
                />
              </label>
              <label className="space-y-2 text-sm font-medium">
                Notes
                <input
                  className={inputClass}
                  value={form.notes}
                  onChange={(e) => field("notes", e.target.value)}
                />
              </label>
            </div>
            {grn.purchaseOrderId && (
              <p className="text-sm text-gray-500">
                Linked PO: {grn.purchaseOrder?.purchaseOrderNumber}. The PO,
                supplier and store remain linked.
              </p>
            )}
            <div className="flex items-center justify-between">
              <h3 className="font-bold">Products ({items.length})</h3>
              {!grn.purchaseOrderId && (
                <button
                  disabled={saving}
                  type="button"
                  onClick={() => setProductPicker(true)}
                  className="flex items-center gap-2 rounded-xl border border-primary-200 px-4 py-2 text-sm font-semibold text-primary-800"
                >
                  <FiPlus />
                  Search & add products
                </button>
              )}
            </div>
            <div className="max-h-[50vh] overflow-auto rounded-xl border border-gray-200">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-primary-800 text-white">
                  <tr>
                    {[
                      "Product / variant",
                      ...numericColumns.map(([, label]) => label),
                      "Supplier batch",
                      "Expiry date",
                      "Line notes",
                      "Default online lot",
                      "Remove",
                    ].map((label) => (
                      <th
                        key={label}
                        className="whitespace-nowrap border border-primary-700 px-3 py-3 text-left"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((line, index) => (
                    <tr key={line.variantId} className="even:bg-gray-50">
                      <td className="min-w-60 border p-3">
                        <p className="font-semibold">
                          {line.variant?.product?.name ||
                            line.variant?.productCode ||
                            line.variant?.sku ||
                            "Product"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {line.variant?.sku} · {line.variant?.variantName}
                        </p>
                        <p className="text-xs text-gray-500">
                          HSN: {line.variant?.product?.hsnCode || "—"} · GST
                          recalculated from product rule
                        </p>
                      </td>
                      {numericColumns.map(([key, label]) => (
                        <td className="border p-2" key={key}>
                          <input
                            aria-label={`${label} row ${index + 1}`}
                            required={[
                              "quantityReceived",
                              "quantityAccepted",
                              "quantityRejected",
                              "unitCost",
                              "posPrice",
                            ].includes(key)}
                            type="number"
                            min={key === "quantityReceived" ? "0.001" : "0"}
                            max={key.endsWith("Percent") ? 100 : undefined}
                            step={key.startsWith("quantity") ? "0.001" : "0.01"}
                            value={line[key]}
                            onChange={(e) =>
                              changeLine(index, key, e.target.value)
                            }
                            className={`${inputClass} min-w-28`}
                          />
                        </td>
                      ))}
                      <td className="border p-2">
                        <input
                          maxLength={100}
                          value={line.batchNumber}
                          onChange={(e) =>
                            changeLine(index, "batchNumber", e.target.value)
                          }
                          className={`${inputClass} min-w-36`}
                        />
                      </td>
                      <td className="border p-2">
                        <input
                          type="date"
                          value={line.expiryDate}
                          onChange={(e) =>
                            changeLine(index, "expiryDate", e.target.value)
                          }
                          className={inputClass}
                        />
                      </td>
                      <td className="border p-2">
                        <input
                          value={line.notes}
                          onChange={(e) =>
                            changeLine(index, "notes", e.target.value)
                          }
                          className={`${inputClass} min-w-36`}
                        />
                      </td>
                      <td className="border p-3 text-center">
                        <input
                          aria-label={`Default online lot row ${index + 1}`}
                          type="checkbox"
                          checked={!!line.isDefaultOnlineLot}
                          onChange={(e) =>
                            changeLine(
                              index,
                              "isDefaultOnlineLot",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td className="border p-3">
                        <button
                          type="button"
                          disabled={saving}
                          aria-label={`Remove product row ${index + 1}`}
                          onClick={() =>
                            setItems((current) =>
                              current.filter((_, i) => i !== index),
                            )
                          }
                          className="p-2 text-red-600"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="block space-y-2 text-sm font-medium">
              Reason for change *
              <textarea
                required
                minLength={5}
                maxLength={500}
                value={form.editReason}
                onChange={(e) => field("editReason", e.target.value)}
                placeholder="Explain what is being corrected"
                className="w-full rounded-xl border border-gray-200 p-3"
              />
            </label>
          </div>
          <footer className="flex items-center justify-between gap-4 border-t bg-gray-50 px-6 py-4">
            <p className="text-xs text-gray-500">
              Discounts, GST and round-off are recalculated by the backend.
              Freight stays separate.
            </p>
            <div className="flex gap-3">
              <button
                disabled={saving}
                type="button"
                onClick={onClose}
                className="rounded-xl border bg-white px-5 py-3 font-semibold"
              >
                Cancel
              </button>
              <button
                disabled={saving || loading}
                type="submit"
                className="flex items-center gap-2 rounded-xl bg-primary-800 px-5 py-3 font-semibold text-white disabled:opacity-50"
              >
                <FiSave />
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </footer>
        </form>
      </div>
      <PurchaseOrderProductFilterModal
        open={productPicker}
        supplierId={form.supplierId}
        accessToken={accessToken}
        onClose={() => setProductPicker(false)}
        onAddItems={addProducts}
      />
    </>
  );
}
